import React from 'react';
import { motion } from 'framer-motion';
import { Baby, Users as UsersIcon, Trophy, School, Clock, Award, Heart } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Services = ({ user, onLogout }) => {
  return (
    <div className="min-h-screen">
      <Navigation user={user} onLogout={onLogout} />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-cyan-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
              Our Swimming Programs
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              From beginners to competitive athletes, we offer comprehensive swimming instruction
              tailored to every age and skill level.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Learn to Swim - Children */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Baby className="w-8 h-8 text-cyan-700" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Learn to Swim - Children</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Our children's program makes learning to swim fun and stress-free. Using age-appropriate
                teaching methods, we help kids develop water confidence while building essential swimming skills.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-cyan-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Flexible Schedule</h3>
                    <p className="text-slate-600">30-45 minute sessions, 2-3 times per week</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-cyan-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Structured Curriculum</h3>
                    <p className="text-slate-600">Progressive levels from water familiarization to stroke development</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-5 h-5 text-cyan-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Small Class Sizes</h3>
                    <p className="text-slate-600">Maximum 6 students per instructor for personalized attention</p>
                  </div>
                </div>
              </div>
              <Link to="/book-lesson">
                <Button className="btn-primary" size="lg" data-testid="book-children-lesson-btn">
                  Book Children's Lesson
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden shadow-2xl border-4 border-cyan-600"
            >
              <img
                src="https://images.pexels.com/photos/6011945/pexels-photo-6011945.jpeg"
                alt="Coach teaching African child to swim"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Learn to Swim - Adults */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden shadow-2xl order-2 lg:order-1 border-4 border-blue-600"
            >
              <img
                src="https://images.pexels.com/photos/31522680/pexels-photo-31522680.jpeg"
                alt="Coach teaching African adult woman to swim"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <UsersIcon className="w-8 h-8 text-blue-700" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Learn to Swim - Adults</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                It's never too late to learn! Our adult program offers a comfortable, non-judgmental
                environment where you can overcome fears and develop swimming skills at your own pace.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-blue-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Private & Group Options</h3>
                    <p className="text-slate-600">Choose one-on-one instruction or small group classes</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-blue-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Patient Instruction</h3>
                    <p className="text-slate-600">Experienced instructors who understand adult learning</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-5 h-5 text-blue-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Fitness & Confidence</h3>
                    <p className="text-slate-600">Build strength, endurance, and water confidence</p>
                  </div>
                </div>
              </div>
              <Link to="/book-lesson">
                <Button className="btn-primary" size="lg" data-testid="book-adult-lesson-btn">
                  Book Adult Lesson
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marlins Swim Team */}
      <section className="py-20 bg-gradient-to-br from-cyan-700 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-orange-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Marlins Swim Team</h2>
              <p className="text-xl text-cyan-50 mb-6 leading-relaxed">
                Our year-round competitive swim program develops champions through dedicated training,
                expert coaching, and a supportive team environment.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <Trophy className="w-5 h-5 text-orange-300 mt-1" />
                  <div>
                    <h3 className="font-semibold">Competitive Training</h3>
                    <p className="text-cyan-100">Prepare for regional and national competitions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-orange-300 mt-1" />
                  <div>
                    <h3 className="font-semibold">Expert Coaching</h3>
                    <p className="text-cyan-100">Train with experienced competitive swimming coaches</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-5 h-5 text-orange-300 mt-1" />
                  <div>
                    <h3 className="font-semibold">Team Spirit</h3>
                    <p className="text-cyan-100">Build lifelong friendships and teamwork skills</p>
                  </div>
                </div>
              </div>
              <Link to="/swim-team">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 rounded-full px-8 py-3 font-semibold"
                  data-testid="join-marlins-btn"
                >
                  Join the Marlins
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden shadow-2xl border-4 border-orange-500"
            >
              <img
                src="https://images.unsplash.com/photo-1527617150257-a08333ab99cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwa2lkcyUyMHN3aW0lMjB0ZWFtJTIwY29tcGV0aXRpdmUlMjB0cmFpbmluZyUyMHBvb2x8ZW58MHx8fHwxNzY5Mjg5NTExfDA&ixlib=rb-4.1.0&q=85"
                alt="African kids competitive swim team training"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* School Partnerships */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://images.unsplash.com/photo-1763930286426-6f17a720253b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMHN3aW1taW5nJTIwcG9vbCUyMHdhdGVyJTIwdGV4dHVyZSUyMGJyaWdodHxlbnwwfHx8fDE3Njc4MTk3NTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Pool water"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-purple-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <School className="w-8 h-8 text-purple-700" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">School Swimming Programs</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Partner with us to bring quality swimming instruction to your school. We design and manage
                comprehensive aquatics programs tailored to your institution's needs.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <School className="w-5 h-5 text-purple-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Customized Programs</h3>
                    <p className="text-slate-600">Tailored curriculum to fit your school's requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-purple-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Professional Management</h3>
                    <p className="text-slate-600">Complete program oversight and administration</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-5 h-5 text-purple-700 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Safe & Reliable</h3>
                    <p className="text-slate-600">Proven track record with educational institutions</p>
                  </div>
                </div>
              </div>
              <Link to="/partnerships">
                <Button className="btn-primary" size="lg" data-testid="partner-btn">
                  Partner With Us
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
